import { Injectable } from '@angular/core';
import { LoggerService } from './logger.service';
import { Subject } from 'rxjs';
import { ChartOptions } from 'chart.js';
import { LocaleService } from './locale.service';
import { ConfigService } from './config.service';
import { HighContrastTokens } from '../_core/accessibility/services/models/accesibility.model';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  theme: string;
  baseUrl: string;
  logoUrl: string;
  logoSeoUrl: string;
  developedUrl: string;
  computedStyle: CSSStyleDeclaration;

  headerLogos$ = new Subject<string[]>();
  headerLogosAlt$ = new Subject<(string|undefined)[]>();
  headerBackground$ = new Subject<string>();
  footerLogos$ = new Subject<string[]>();
  footerLogosAlt$ = new Subject<string[]>();

  logoExpandedUrl: string;
  sideLeftUrl: string;
  sideRightUrl: string;
  imageCenterUrl: string;

  selfPublicationBannerUrl: string;
  twentyFourSymbolsBannerUrl: string;
  remoteLoginBannerUrl: string;

  constructor(
    private logger: LoggerService,
    private configService: ConfigService,
    private localeService: LocaleService
  ) {}

  set(theme: string) {
    this.theme = theme || 'default';

    const baseUrl = `https://themes.odilo.io/${this.theme}`;
    const nonSecuredBaseUrl = `http://themes.odilo.io/${this.theme}`;
    const themeUrl = baseUrl + '/_theme.css';

    // general
    this.logoUrl = this.getLogoUrlWithLocale(baseUrl);
    this.logoSeoUrl = `${nonSecuredBaseUrl}/images/1200x630.png`;
    this.developedUrl = `${baseUrl}/images/developed.svg`;
    this.sideLeftUrl = `${baseUrl}/images/side_left.svg`;
    this.sideRightUrl = `${baseUrl}/images/side_right.svg`;
    this.imageCenterUrl = `${baseUrl}/images/img_center01.svg`;
    this.selfPublicationBannerUrl = `${baseUrl}/images/banner01.png`;
    this.twentyFourSymbolsBannerUrl = `${baseUrl}/images/banner02.png`;
    this.remoteLoginBannerUrl = `${baseUrl}/images/banner03.png`;

    // welcome screen
    this.logoExpandedUrl = `${baseUrl}/images/logo-expanded.svg`;
    this.baseUrl = baseUrl;

    this.injectTheme(themeUrl);
    this.injectHighContrastThemes();
  }

  getLogoUrlWithLocale(baseUrl: string): string {
    if (this.configService.get()?.enableLogoWithLocale) {
      const localeForLogo = this.getLocaleForLogo();
      return `${baseUrl}/images/logo_${localeForLogo.toUpperCase()}.png`;
    } else {
      return `${baseUrl}/images/logo.png`;
    }
  }

  getLocaleForLogo(): string {
    if (this.configService.get().languages.includes(this.localeService.language)) {
      return this.localeService.language;
    } else {
      return 'en';
    }
  }

  getChartTheme(): ChartTheme {
    const accentBackground = this.computedStyle.getPropertyValue('--button-accent-background');
    const primaryFont = this.computedStyle.getPropertyValue('--primary-font');
    const fontCaption = this.computedStyle.getPropertyValue('--font-caption-color');

    return {
      colors: [{ backgroundColor: accentBackground }],
      options: {
        animation: {
          duration: 500,
        },
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            displayColors: false,
            callbacks: {
              title: () => null,
            },
          },
        },
        scales: {
          x: {
            grid: {
              display: false,
              color: primaryFont,
            },
            ticks: {
              stepSize: 10,
              color: primaryFont,
            },
          },
          y: {
            max: 60,
            grid: {
              color: fontCaption,
            },
            ticks: {
              color: primaryFont,
              padding: 10,
            },
          },
        },
      },
    };
  }

  private injectTheme(themeUrl: string) {
    this.logger.info('Using custom theme from', themeUrl);
    const cssElement = document.createElement('link');
    cssElement.rel = 'stylesheet';
    cssElement.href = themeUrl;
    cssElement.onload = () => {
      this.computedStyle = window.getComputedStyle(document.body);
      this.setAndroidThemeColor();
      this.setFontStyle();
      this.setCustomLogos();
    };

    cssElement.onerror = () => this.logger.warn(`Cannot load custom CSS: ${themeUrl}. Using default style.`);

    document.head.appendChild(cssElement);
  }

  private injectHighContrastThemes() {
    const themes = Object.values(HighContrastTokens);

    themes.forEach(theme => {
      const themeUrl = `https://themes.odilo.io/${theme.themeUrl}/_theme.css`;
      const cssElement = document.createElement('link');
      cssElement.rel = 'stylesheet';
      cssElement.href = themeUrl;

      cssElement.onerror = () => this.logger.warn(`Cannot load custom CSS: ${themeUrl}. Using default style.`);

      document.head.appendChild(cssElement);
    });
  }

  private setAndroidThemeColor() {
    const metaColor = this.computedStyle.backgroundColor;
    const metaElement = document.createElement('meta');
    metaElement.name = 'theme-color';
    metaElement.content = metaColor;
    document.head.appendChild(metaElement);
  }

  private setFontStyle() {
    const fontVariables = ['--font-regular', '--font-medium', '--font-bold', '--font-name'];

    let fontStyle = document.querySelector('#font-style').innerHTML;
    fontVariables.forEach(
      (v) => (fontStyle = fontStyle.replace(new RegExp(v, 'g'), this.computedStyle.getPropertyValue(v)))
    );

    document.querySelector('#font-style').innerHTML = fontStyle;
  }

  private setCustomLogos() {
    const header = Number(this.computedStyle.getPropertyValue('--header-logos'));
    const headerBackground = this.computedStyle.getPropertyValue('--header-background-image');
    const footer = Number(this.computedStyle.getPropertyValue('--footer-logos'));

    const headerLogos = [];
    const headerLogosAlt = [];
    const footerLogos = [];
    const footerLogosAlt = [];

    Array.from(Array(header).keys()).forEach((i) =>
      headerLogos.push(this.getHeaderLogoUrlWithLocale(this.baseUrl, i + 1))
    );

    // Add alt text to "Aula digital Madrid"
    if (this.configService.get().codCliente === 'E1155') {
      headerLogosAlt.push("Madrid Aula Digital");
      headerLogosAlt.push("Financiado por la Unión Europea de NextGenerationEU. Ministerio de Transformación Digital y Función Pública del Gobierno de España. Plan de Recuperación, Transformación y Resiliencia. Comunidad de Madrid");
    } else {
      headerLogos.forEach(() => headerLogosAlt.push(undefined));
    }

    this.headerLogos$.next(headerLogos);
    this.headerLogosAlt$.next(headerLogosAlt);
    this.headerBackground$.next(headerBackground);

    Array.from(Array(footer).keys()).forEach((i) =>
      footerLogos.push(this.getFooterLogoUrlWithLocale(this.baseUrl, i + 1))
    );

    // Add alt text to "Aula digital Madrid"
    if (this.configService.get().codCliente === 'E1155') {
      footerLogosAlt.push('Financiado por la Unión Europea');
      footerLogosAlt.push('Ministerio para la transformación digital y de la función pública');
      footerLogosAlt.push('Plan de recuperación, Transformación y Resiliencia');
      footerLogosAlt.push('Comunidad de Madrid');
    } else {
      footerLogos.forEach(() => footerLogosAlt.push(undefined));
    }

    this.footerLogos$.next(footerLogos);
    this.footerLogosAlt$.next(footerLogosAlt);
  }

  getHeaderLogoUrlWithLocale(baseUrl: string, headerIndex: number): string {
    if (this.configService.get()?.enableLogoWithLocale) {
      const localeForLogo = this.getLocaleForLogo();
      return `${baseUrl}/images/header${headerIndex}_${localeForLogo.toUpperCase()}.png`;
    } else {
      return `${baseUrl}/images/header${headerIndex}.png`;
    }
  }

  getFooterLogoUrlWithLocale(baseUrl: string, footerIndex: number): string {
    if (this.configService.get()?.enableLogoWithLocale) {
      const localeForLogo = this.getLocaleForLogo();
      return `${baseUrl}/images/footer${footerIndex}_${localeForLogo.toUpperCase()}.png`;
    } else {
      return `${baseUrl}/images/footer${footerIndex}.png`;
    }
  }
}

export interface ChartTheme {
  colors: any[];
  options: ChartOptions;
}
